import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, Send, Inbox, Star, Archive, Trash2, Search, Plus, 
  ArrowLeft, Paperclip, Clock, CheckCheck, AlertCircle, 
  User, Calendar, Loader2, ChevronLeft, ChevronRight,
  Reply, MoreVertical, X, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Priority colors
const PRIORITY_COLORS = {
  urgent: { bg: 'bg-red-500/20', text: 'text-red-500', label: 'عاجل', labelEn: 'Urgent' },
  normal: { bg: 'bg-blue-500/20', text: 'text-blue-500', label: 'عادي', labelEn: 'Normal' },
  low: { bg: 'bg-gray-500/20', text: 'text-gray-500', label: 'منخفض', labelEn: 'Low' }
};

export default function Messages() {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { language } = useLanguage();
  
  // State
  const [activeTab, setActiveTab] = useState('inbox'); // inbox, sent, starred, archived
  const [activeView, setActiveView] = useState('list'); // list, compose, read
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ unread: 0, inbox_total: 0, sent_total: 0, starred: 0, archived: 0 });
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Compose state
  const [composeData, setComposeData] = useState({
    recipient_id: '',
    subject: '',
    body: '',
    priority: 'normal',
    attachments: []
  });
  const [sending, setSending] = useState(false);
  const [replyMode, setReplyMode] = useState(false);
  
  const headers = { Authorization: `Bearer ${token}` };

  // Fetch messages based on active tab
  const fetchMessages = async () => {
    setLoading(true);
    try {
      let endpoint = '/api/messages/inbox';
      let params = { page: currentPage, limit: 15 };
      
      if (activeTab === 'sent') {
        endpoint = '/api/messages/sent';
      } else if (activeTab === 'starred') {
        endpoint = '/api/messages/inbox';
        params.is_starred = true;
      } else if (activeTab === 'archived') {
        endpoint = '/api/messages/inbox';
        params.is_archived = true;
      }
      
      const res = await axios.get(`${BACKEND_URL}${endpoint}`, { headers, params });
      setMessages(res.data.messages || []);
      setTotalPages(res.data.total_pages || 1);
    } catch (err) {
      console.error('Error fetching messages:', err);
      toast.error(language === 'ar' ? 'فشل في جلب الرسائل' : 'Failed to fetch messages');
    } finally {
      setLoading(false);
    }
  };

  // Fetch stats
  const fetchStats = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/messages/stats`, { headers });
      setStats(res.data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch users for compose
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/messages/users`, { headers });
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  useEffect(() => {
    fetchStats();
    fetchUsers();
  }, []);

  useEffect(() => {
    fetchMessages();
  }, [activeTab, currentPage]);

  // Send message
  const handleSend = async () => {
    if (!composeData.recipient_id || !composeData.subject || !composeData.body) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    
    setSending(true);
    try {
      if (replyMode && selectedMessage) {
        await axios.post(
          `${BACKEND_URL}/api/messages/${selectedMessage.id}/reply`,
          { body: composeData.body, attachments: composeData.attachments },
          { headers }
        );
      } else {
        await axios.post(`${BACKEND_URL}/api/messages/send`, composeData, { headers });
      }
      
      toast.success(language === 'ar' ? 'تم إرسال الرسالة بنجاح' : 'Message sent successfully');
      setActiveView('list');
      setComposeData({ recipient_id: '', subject: '', body: '', priority: 'normal', attachments: [] });
      setReplyMode(false);
      fetchMessages();
      fetchStats();
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في إرسال الرسالة' : 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Open message
  const openMessage = async (message) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/messages/${message.id}`, { headers });
      setSelectedMessage(res.data.message);
      setActiveView('read');
      if (!message.is_read) {
        fetchStats();
        fetchMessages();
      }
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في فتح الرسالة' : 'Failed to open message');
    }
  };

  // Toggle starred
  const toggleStarred = async (message, e) => {
    e.stopPropagation();
    try {
      await axios.put(
        `${BACKEND_URL}/api/messages/${message.id}`,
        { is_starred: !message.is_starred },
        { headers }
      );
      fetchMessages();
      fetchStats();
    } catch (err) {
      console.error('Error toggling starred:', err);
    }
  };

  // Archive message
  const archiveMessage = async (message, e) => {
    e?.stopPropagation();
    try {
      await axios.put(
        `${BACKEND_URL}/api/messages/${message.id}`,
        { is_archived: true },
        { headers }
      );
      toast.success(language === 'ar' ? 'تم أرشفة الرسالة' : 'Message archived');
      fetchMessages();
      fetchStats();
      if (activeView === 'read') {
        setActiveView('list');
        setSelectedMessage(null);
      }
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في أرشفة الرسالة' : 'Failed to archive message');
    }
  };

  // Delete message
  const deleteMessage = async (message, e) => {
    e?.stopPropagation();
    try {
      await axios.delete(`${BACKEND_URL}/api/messages/${message.id}`, { headers });
      toast.success(language === 'ar' ? 'تم حذف الرسالة' : 'Message deleted');
      fetchMessages();
      fetchStats();
      if (activeView === 'read') {
        setActiveView('list');
        setSelectedMessage(null);
      }
    } catch (err) {
      toast.error(language === 'ar' ? 'فشل في حذف الرسالة' : 'Failed to delete message');
    }
  };

  // Reply to message
  const handleReply = () => {
    if (!selectedMessage) return;
    
    const recipientId = selectedMessage.sender_id === user?.id 
      ? selectedMessage.recipient_id 
      : selectedMessage.sender_id;
    
    setComposeData({
      recipient_id: recipientId,
      subject: selectedMessage.subject.startsWith('رد:') ? selectedMessage.subject : `رد: ${selectedMessage.subject}`,
      body: '',
      priority: selectedMessage.priority,
      attachments: []
    });
    setReplyMode(true);
    setActiveView('compose');
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString('ar-AE', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return language === 'ar' ? 'أمس' : 'Yesterday';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('ar-AE', { weekday: 'long' });
    } else {
      return date.toLocaleDateString('ar-AE', { month: 'short', day: 'numeric' });
    }
  };

  // Tabs
  const tabs = [
    { id: 'inbox', icon: Inbox, label: 'الوارد', labelEn: 'Inbox', count: stats.unread },
    { id: 'sent', icon: Send, label: 'المرسل', labelEn: 'Sent', count: 0 },
    { id: 'starred', icon: Star, label: 'المميز', labelEn: 'Starred', count: stats.starred },
    { id: 'archived', icon: Archive, label: 'الأرشيف', labelEn: 'Archived', count: stats.archived }
  ];

  return (
    <DashboardLayout
      title="المراسلات"
      titleEn="Messages"
      subtitle="نظام المراسلات الداخلية"
      subtitleEn="Internal messaging system"
      screenCode="MSG-001"
      backPath="/employee-dashboard"
      breadcrumb={[
        { labelAr: 'لوحة تحكم الموظف', labelEn: 'Employee Dashboard', path: '/employee-dashboard' },
        { labelAr: 'المراسلات', labelEn: 'Messages', path: null }
      ]}
    >
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className="w-64 flex-shrink-0">
            {/* Compose Button */}
            <Button
              onClick={() => {
                setActiveView('compose');
                setReplyMode(false);
                setComposeData({ recipient_id: '', subject: '', body: '', priority: 'normal', attachments: [] });
              }}
              className="w-full bg-gradient-to-r from-[#d4af37] to-[#c9a227] hover:from-[#c9a227] hover:to-[#b8922a] text-black font-bold py-3 gap-2 mb-6"
              data-testid="compose-btn"
            >
              <Plus className="h-5 w-5" />
              {language === 'ar' ? 'رسالة جديدة' : 'New Message'}
            </Button>

            {/* Navigation Tabs */}
            <nav className="space-y-1">
              {tabs.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id && activeView === 'list';
                return (
                  <button
                    key={tab.id}
                    onClick={() => {
                      setActiveTab(tab.id);
                      setActiveView('list');
                      setCurrentPage(1);
                    }}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all ${
                      isActive
                        ? 'bg-[#d4af37]/20 text-[#d4af37] border border-[#d4af37]/30'
                        : 'text-[#808080] hover:bg-white/[0.05] hover:text-white'
                    }`}
                    data-testid={`tab-${tab.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="h-5 w-5" />
                      <span className="font-medium">{language === 'ar' ? tab.label : tab.labelEn}</span>
                    </div>
                    {tab.count > 0 && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                        isActive ? 'bg-[#d4af37] text-black' : 'bg-white/10 text-white'
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 bg-white/[0.02] backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
            <AnimatePresence mode="wait">
              {/* List View */}
              {activeView === 'list' && (
                <motion.div
                  key="list"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                >
                  {/* Search Bar */}
                  <div className="p-4 border-b border-white/10">
                    <div className="relative">
                      <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#606060]" />
                      <Input
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={language === 'ar' ? 'بحث في الرسائل...' : 'Search messages...'}
                        className="pr-10 bg-black/30 border-white/10 text-white placeholder:text-[#606060]"
                      />
                    </div>
                  </div>

                  {/* Messages List */}
                  {loading ? (
                    <div className="flex items-center justify-center py-20">
                      <Loader2 className="h-8 w-8 animate-spin text-[#d4af37]" />
                    </div>
                  ) : messages.length === 0 ? (
                    <div className="text-center py-20 text-[#808080]">
                      <Mail className="h-16 w-16 mx-auto mb-4 opacity-30" />
                      <p className="text-lg">{language === 'ar' ? 'لا توجد رسائل' : 'No messages'}</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-white/5">
                      {messages.map(message => {
                        const priority = PRIORITY_COLORS[message.priority] || PRIORITY_COLORS.normal;
                        const isUnread = !message.is_read && activeTab === 'inbox';
                        
                        return (
                          <motion.div
                            key={message.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            onClick={() => openMessage(message)}
                            className={`flex items-center gap-4 px-4 py-4 cursor-pointer transition-all hover:bg-white/[0.03] ${
                              isUnread ? 'bg-[#d4af37]/5' : ''
                            }`}
                          >
                            {/* Star */}
                            <button
                              onClick={(e) => toggleStarred(message, e)}
                              className={`p-1 rounded transition-colors ${
                                message.is_starred ? 'text-amber-500' : 'text-[#606060] hover:text-amber-500'
                              }`}
                            >
                              <Star className={`h-5 w-5 ${message.is_starred ? 'fill-current' : ''}`} />
                            </button>

                            {/* Avatar */}
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#d4af37] to-[#c9a227] flex items-center justify-center text-black font-bold">
                              {(activeTab === 'sent' ? message.recipient_name : message.sender_name)?.charAt(0).toUpperCase()}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`font-medium truncate ${isUnread ? 'text-white' : 'text-[#a0a0a0]'}`}>
                                  {activeTab === 'sent' ? message.recipient_name : message.sender_name}
                                </span>
                                {message.priority === 'urgent' && (
                                  <AlertCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
                                )}
                              </div>
                              <p className={`truncate ${isUnread ? 'text-white font-medium' : 'text-[#808080]'}`}>
                                {message.subject}
                              </p>
                              <p className="text-sm text-[#606060] truncate">{message.body}</p>
                            </div>

                            {/* Meta */}
                            <div className="flex flex-col items-end gap-1 flex-shrink-0">
                              <span className="text-xs text-[#606060]">{formatDate(message.created_at)}</span>
                              {message.is_read && activeTab !== 'sent' && (
                                <CheckCheck className="h-4 w-4 text-emerald-500" />
                              )}
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                              <button
                                onClick={(e) => archiveMessage(message, e)}
                                className="p-2 rounded-lg hover:bg-white/10 text-[#606060] hover:text-white transition-colors"
                              >
                                <Archive className="h-4 w-4" />
                              </button>
                              <button
                                onClick={(e) => deleteMessage(message, e)}
                                className="p-2 rounded-lg hover:bg-red-500/10 text-[#606060] hover:text-red-500 transition-colors"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </motion.div>
                        );
                      })}
                    </div>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-center gap-4 p-4 border-t border-white/10">
                      <Button
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-white"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <span className="text-white text-sm">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        variant="outline"
                        size="sm"
                        className="border-white/10 text-white"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Compose View */}
              {activeView === 'compose' && (
                <motion.div
                  key="compose"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-6"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => setActiveView('list')}
                        className="p-2 rounded-lg hover:bg-white/10 text-[#808080] hover:text-white transition-colors"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </button>
                      <h2 className="text-xl font-bold text-white">
                        {replyMode 
                          ? (language === 'ar' ? 'رد على الرسالة' : 'Reply to Message')
                          : (language === 'ar' ? 'رسالة جديدة' : 'New Message')
                        }
                      </h2>
                    </div>
                    <Button
                      onClick={handleSend}
                      disabled={sending}
                      className="bg-gradient-to-r from-[#d4af37] to-[#c9a227] text-black font-bold gap-2"
                      data-testid="send-btn"
                    >
                      {sending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      {language === 'ar' ? 'إرسال' : 'Send'}
                    </Button>
                  </div>

                  {/* Form */}
                  <div className="space-y-4">
                    {/* Recipient */}
                    <div>
                      <label className="block text-sm text-[#808080] mb-2">
                        {language === 'ar' ? 'إلى' : 'To'} *
                      </label>
                      <select
                        value={composeData.recipient_id}
                        onChange={(e) => setComposeData(prev => ({ ...prev, recipient_id: e.target.value }))}
                        className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white focus:border-[#d4af37] focus:outline-none"
                        disabled={replyMode}
                        data-testid="recipient-select"
                      >
                        <option value="" style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>
                          {language === 'ar' ? 'اختر المستلم...' : 'Select recipient...'}
                        </option>
                        {users.map(u => (
                          <option key={u.id} value={u.id} style={{ backgroundColor: '#1a1a2e', color: '#ffffff' }}>
                            {u.full_name || u.username} ({u.role})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Subject */}
                    <div>
                      <label className="block text-sm text-[#808080] mb-2">
                        {language === 'ar' ? 'الموضوع' : 'Subject'} *
                      </label>
                      <Input
                        value={composeData.subject}
                        onChange={(e) => setComposeData(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder={language === 'ar' ? 'أدخل موضوع الرسالة...' : 'Enter message subject...'}
                        className="bg-black/30 border-white/10 text-white placeholder:text-[#606060]"
                        disabled={replyMode}
                        data-testid="subject-input"
                      />
                    </div>

                    {/* Priority */}
                    <div>
                      <label className="block text-sm text-[#808080] mb-2">
                        {language === 'ar' ? 'الأولوية' : 'Priority'}
                      </label>
                      <div className="flex gap-2">
                        {Object.entries(PRIORITY_COLORS).map(([key, value]) => (
                          <button
                            key={key}
                            onClick={() => setComposeData(prev => ({ ...prev, priority: key }))}
                            className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                              composeData.priority === key
                                ? `${value.bg} ${value.text} border border-current`
                                : 'bg-white/[0.02] text-[#808080] border border-white/10 hover:border-white/20'
                            }`}
                          >
                            {language === 'ar' ? value.label : value.labelEn}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Body */}
                    <div>
                      <label className="block text-sm text-[#808080] mb-2">
                        {language === 'ar' ? 'الرسالة' : 'Message'} *
                      </label>
                      <textarea
                        value={composeData.body}
                        onChange={(e) => setComposeData(prev => ({ ...prev, body: e.target.value }))}
                        placeholder={language === 'ar' ? 'اكتب رسالتك هنا...' : 'Write your message here...'}
                        className="w-full h-64 bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-[#606060] focus:border-[#d4af37] focus:outline-none resize-none"
                        data-testid="body-input"
                      />
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Read View */}
              {activeView === 'read' && selectedMessage && (
                <motion.div
                  key="read"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="p-6"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setActiveView('list');
                          setSelectedMessage(null);
                        }}
                        className="p-2 rounded-lg hover:bg-white/10 text-[#808080] hover:text-white transition-colors"
                      >
                        <ArrowLeft className="h-5 w-5" />
                      </button>
                      <h2 className="text-xl font-bold text-white truncate">{selectedMessage.subject}</h2>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={handleReply}
                        variant="outline"
                        className="border-white/10 text-white hover:bg-white/10 gap-2"
                        data-testid="reply-btn"
                      >
                        <Reply className="h-4 w-4" />
                        {language === 'ar' ? 'رد' : 'Reply'}
                      </Button>
                      <button
                        onClick={() => archiveMessage(selectedMessage)}
                        className="p-2 rounded-lg hover:bg-white/10 text-[#808080] hover:text-white transition-colors"
                      >
                        <Archive className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => deleteMessage(selectedMessage)}
                        className="p-2 rounded-lg hover:bg-red-500/10 text-[#808080] hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="h-5 w-5" />
                      </button>
                    </div>
                  </div>

                  {/* Message Info */}
                  <div className="bg-black/30 rounded-xl p-4 mb-6 border border-white/5">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#d4af37] to-[#c9a227] flex items-center justify-center text-black font-bold text-lg">
                        {selectedMessage.sender_name?.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-white">{selectedMessage.sender_name}</span>
                          <span className="text-xs text-[#606060]">@{selectedMessage.sender_username}</span>
                          {selectedMessage.priority === 'urgent' && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-red-500/20 text-red-500">
                              {language === 'ar' ? 'عاجل' : 'Urgent'}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-[#808080]">
                          <span className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {language === 'ar' ? 'إلى:' : 'To:'} {selectedMessage.recipient_name}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {new Date(selectedMessage.created_at).toLocaleString('ar-AE')}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Message Body */}
                  <div className="bg-white/[0.02] rounded-xl p-6 border border-white/5">
                    <p className="text-white whitespace-pre-wrap leading-relaxed">
                      {selectedMessage.body}
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </section>
    </DashboardLayout>
  );
}
